import React, { useState, useEffect } from 'react';
import { Modal } from '../../common/Modal';
import { User, Users, Search, Building2, Scale as Male, Scale as Female } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useGuidesStore } from '../../../stores/guidesStore';
import toast from 'react-hot-toast';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { Flag } from '../../common/Flag';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (data: {
    name: string;
    gender: 'M' | 'F';
    isGuide: boolean;
    guideId?: string;
    guideName?: string;
    organizerId?: string;
    organizerName?: string;
  }) => void;
};

export const EmployeeGuideModal = ({ isOpen, onClose, onAssign }: Props) => {
  const [tab, setTab] = useState<'employee' | 'guide'>('guide');
  const [employeeName, setEmployeeName] = useState('');
  const [employeeGender, setEmployeeGender] = useState<'M' | 'F'>('M');
  const { translations } = useLanguage();
  const { guides, loading: guidesLoading, initialized: guidesInitialized, initializeListener } = useGuidesStore();
  const [organizers, setOrganizers] = useState<Record<string, any>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuideId, setSelectedGuideId] = useState<string | null>(null);

  // Initialize guides data
  useEffect(() => {
    if (!guidesInitialized) {
      initializeListener();
    }
  }, [guidesInitialized, initializeListener]);

  // Load organizers for displaying with guides
  useEffect(() => {
    const fetchOrganizers = async () => {
      try {
        const snapshot = await getDocs(collection(firestore, 'Organizers'));
        const organizersData: Record<string, any> = {};
        
        snapshot.forEach(doc => {
          organizersData[doc.id] = { id: doc.id, ...doc.data() };
        });
        
        setOrganizers(organizersData);
      } catch (error) {
        console.error('Error fetching organizers:', error);
      }
    };
    
    fetchOrganizers();
  }, []);

  const handleEmployeeSubmit = () => {
    if (!employeeName.trim()) {
      toast.error('Employee name is required');
      return;
    }

    onAssign({
      name: employeeName.trim(),
      gender: employeeGender,
      isGuide: false,
    });
    
    toast.success('Employee assigned successfully');
    onClose();
  };

  const handleGuideSubmit = () => {
    if (!selectedGuideId) {
      toast.error('Please select a guide');
      return;
    }

    const guide = guides[selectedGuideId];
    const organizer = organizers[guide.organizerId];
    
    onAssign({
      name: guide.name,
      gender: 'M', // Default gender - can be improved with actual guide gender data
      isGuide: true,
      guideId: guide.id,
      guideName: guide.name,
      organizerId: guide.organizerId,
      organizerName: organizer?.name || 'Unknown Organizer',
    });
    
    toast.success('Guide assigned successfully');
    onClose();
  };

  // Filter guides based on search term
  const filteredGuides = Object.values(guides).filter(guide => 
    !searchTerm || 
    guide.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    guide.email.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.accommodationRoom?.accommodateGuideEmployee || "Accommodate Guide or Employee"}
    >
      {/* Tab Selection */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => setTab('guide')}
          className={`flex-1 py-3 text-center font-medium transition-colors relative
            ${tab === 'guide' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <div className="flex items-center justify-center gap-2">
            <Users size={20} />
            <span>{translations?.accommodationRoom?.guide || 'Guide'}</span>
          </div>
          {tab === 'guide' && (
            <motion.div 
              layoutId="activeTab" 
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" 
            />
          )}
        </button>
        <button
          onClick={() => setTab('employee')}
          className={`flex-1 py-3 text-center font-medium transition-colors relative
            ${tab === 'employee' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <div className="flex items-center justify-center gap-2">
            <User size={20} />
            <span>{translations?.accommodationRoom?.employee || 'Employee'}</span>
          </div>
          {tab === 'employee' && (
            <motion.div 
              layoutId="activeTab" 
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" 
            />
          )}
        </button>
      </div>

      {/* Employee Tab Content */}
      {tab === 'employee' && (
        <div className="space-y-6">
          {/* Employee Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {translations?.accommodationRoom?.employeeName || 'Employee Name'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="text"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              placeholder={translations?.accommodationRoom?.enterEmployeeName || 'Enter employee name'}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Gender Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {translations?.accommodationRoom?.gender || 'Gender'}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={employeeGender === 'M'}
                  onChange={() => setEmployeeGender('M')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex items-center gap-1 text-sm text-gray-700">
                  <Male size={16} className="text-blue-500" />
                  <span>{translations?.accommodationRoom?.male || 'Male'}</span>
                </div>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={employeeGender === 'F'}
                  onChange={() => setEmployeeGender('F')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex items-center gap-1 text-sm text-gray-700">
                  <Female size={16} className="text-pink-500" />
                  <span>{translations?.accommodationRoom?.female || 'Female'}</span>
                </div>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleEmployeeSubmit}
              disabled={!employeeName.trim()}
              className={`px-6 py-2 rounded-lg text-white ${
                !employeeName.trim() 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {translations?.accommodationRoom?.assignEmployee || 'Assign Employee'}
            </motion.button>
          </div>
        </div>
      )}

      {/* Guide Tab Content */}
      {tab === 'guide' && (
        <div className="space-y-6">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={translations?.accommodationRoom?.searchGuides || 'Search guides...'}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {/* Guides List */}
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-[300px] overflow-y-auto">
              {guidesLoading ? (
                <div className="p-4 text-center text-gray-500">
                  {translations?.accommodationRoom?.loadingGuides || 'Loading guides...'}
                </div>
              ) : filteredGuides.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm 
                    ? (translations?.accommodationRoom?.noGuidesFound || 'No guides found') 
                    : (translations?.accommodationRoom?.noGuides || 'No guides available')}
                </div>
              ) : (
                filteredGuides.map(guide => {
                  const organizer = organizers[guide.organizerId];
                  const isSelected = selectedGuideId === guide.id;

                  return (
                    <motion.div
                      key={guide.id}
                      whileHover={{ backgroundColor: isSelected ? undefined : 'rgba(236, 239, 241, 0.6)' }}
                      onClick={() => setSelectedGuideId(guide.id)}
                      className={`p-4 cursor-pointer border-b last:border-b-0 transition-colors ${
                        isSelected ? 'bg-primary-50 border-primary-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary-50 rounded-full">
                            <User className="text-primary-500" size={18} />
                          </div>
                          <div>
                            <div className="font-medium">{guide.name}</div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Building2 size={14} />
                              <span>{organizer?.name || 'Unknown Organizer'}</span>
                              {organizer?.country && (
                                <Flag countryCode={organizer.country} size={14} />
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center">
                            {isSelected && (
                              <motion.div 
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="w-3 h-3 bg-primary-500 rounded-full" 
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGuideSubmit}
              disabled={!selectedGuideId}
              className={`px-6 py-2 rounded-lg text-white ${
                !selectedGuideId 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {translations?.accommodationRoom?.assignGuide || 'Assign Guide'}
            </motion.button>
          </div>
        </div>
      )}
    </Modal>
  );
};